import React, { useEffect, lazy } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Sun, Shield, Users, Bed, Bath, Square, ShoppingBag, Utensils, Mountain, GraduationCap, BookOpen, Trophy, TreePine, Heart, Car, Plane } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
import { SEOHead } from '../components/SEOHead';
import { RelatedCommunities } from '../components/RelatedCommunities';
const placeholderListings = [{
  id: 1,
  price: '$685,000',
  address: '2847 Grassy Spring Pl',
  area: 'Green Valley Ranch',
  beds: 4,
  baths: 3,
  sqft: '2,650',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 2,
  price: '$549,000',
  address: '1093 Amber Falls Ln',
  area: 'Anthem',
  beds: 3,
  baths: 2.5,
  sqft: '2,100',
  image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
}, {
  id: 3,
  price: '$1,450,000',
  address: '15 Hawk Ridge Dr',
  area: 'MacDonald Highlands',
  beds: 5,
  baths: 5,
  sqft: '4,200',
  image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=2940&auto=format&fit=crop'
}];
export function HendersonPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-766">
      <SEOHead title="Henderson NV Real Estate & Homes for Sale | Jenny Gong" description="Find your dream home in Henderson, Nevada. Consistently ranked among America's safest cities, offering luxury living, top schools, and incredible amenities." keywords="Henderson real estate, Henderson homes for sale, Green Valley Ranch real estate, Anthem homes for sale, Henderson NV realtor" canonicalUrl="https://jennygong.com/henderson" ogImage="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2906&auto=format&fit=crop" data-id="element-767" />
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-768">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2906&auto=format&fit=crop")'
      }} data-id="element-769">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-770"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-771"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-772">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-773">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-774">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-775">
              Henderson Homes for Sale
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-776">
              Nevada's second-largest city, consistently ranked among America's
              safest and most livable communities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 px-6 bg-white" data-id="element-777">
        <div className="max-w-7xl mx-auto" data-id="element-778">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-779">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-780">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-781">
                A City That <span className="italic text-gold" data-id="element-782">Has It All</span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-783">
                <p data-id="element-784">
                  Henderson is Nevada's second-largest city with over 320,000
                  residents, and it's consistently ranked among the safest
                  cities in America by Forbes and the FBI. Located just
                  southeast of the Las Vegas Strip, Henderson offers the perfect
                  blend of suburban tranquility and urban convenience.
                </p>
                <p data-id="element-785">
                  The city features a diverse real estate market ranging from
                  $350K starter homes to $10M+ luxury estates in{' '}
                  <Link to="/macdonald-highlands" className="text-gold hover:underline" data-id="element-786">
                    MacDonald Highlands
                  </Link>
                  . With excellent schools, world-class healthcare at Henderson
                  Hospital and St. Rose Dominican, and a thriving local economy,
                  Henderson attracts families, professionals, and retirees
                  alike.
                </p>
                <p data-id="element-787">
                  Henderson's Water Street District is undergoing a massive
                  revitalization, bringing new dining, entertainment, and
                  cultural venues to the city's historic downtown core. Nearby
                  communities like{' '}
                  <Link to="/green-valley" className="text-gold hover:underline" data-id="element-788">
                    Green Valley
                  </Link>{' '}
                  and{' '}
                  <Link to="/southern-highlands" className="text-gold hover:underline" data-id="element-789">
                    Southern Highlands
                  </Link>{' '}
                  complement Henderson's appeal.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-790">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-791">
                <Users className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-792" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-793">
                  320K+
                </h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-794">
                  Residents
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-795">
                <Shield className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-796" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-797">#2</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-798">
                  Safest Large City
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-799">
                <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-800" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-801">15m</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-802">
                  To The Strip
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-803">
                <Heart className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-804" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-805">Top</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-806">
                  Most Livable
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Neighborhoods */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-807">
        <div className="max-w-7xl mx-auto" data-id="element-808">
          <div className="text-center mb-16" data-id="element-809">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-810">
              Explore Henderson
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-811">
              Henderson's Premier Neighborhoods
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-812">
            {[{
            name: 'Green Valley Ranch',
            price: '$450K - $900K',
            desc: "One of Henderson's most beloved communities with lush parks, excellent schools, and the upscale District shopping center.",
            img: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
          }, {
            name: 'Anthem',
            price: '$400K - $1.2M',
            desc: 'A stunning master-planned community featuring Anthem Country Club, miles of trails, and panoramic valley views.',
            img: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2940&auto=format&fit=crop'
          }, {
            name: 'MacDonald Highlands',
            price: '$1M - $10M+',
            desc: 'Ultra-luxury gated enclave with DragonRidge Country Club, custom estates, and the most spectacular city views in Vegas.',
            img: 'https://images.unsplash.com/photo-1613490908571-9ce224083933?q=80&w=2838&auto=format&fit=crop'
          }, {
            name: 'Seven Hills',
            price: '$500K - $1.5M',
            desc: 'An exclusive guard-gated community with a Rio Secco Golf Club membership option and elegant Mediterranean homes.',
            img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
          }, {
            name: 'Lake Las Vegas',
            price: '$400K - $3M+',
            desc: 'A resort community built around a 320-acre private lake with luxury hotels, golf, and waterfront dining.',
            img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2906&auto=format&fit=crop'
          }, {
            name: 'Cadence',
            price: '$350K - $600K',
            desc: 'A vibrant newer master-planned community with modern homes, a 50-acre central park, and innovative amenities.',
            img: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
          }, {
            name: 'Inspirada',
            price: '$350K - $600K',
            desc: 'A vibrant master-planned community in south Henderson featuring modern homes, community pools, excellent parks, and top-rated schools near Pinecrest Academy.',
            img: 'https://images.unsplash.com/photo-1600573472592-401b489a3cdc?q=80&w=2940&auto=format&fit=crop'
          }].map((n, idx) => <motion.div key={n.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white p-6 shadow-sm border border-warmGray-dark" data-id="element-813">
                <img src={n.img} alt={n.name} className="w-full h-48 object-cover mb-4" data-id="element-814" />
                <div className="flex justify-between items-start mb-3" data-id="element-815">
                  <h3 className="text-xl font-serif text-charcoal" data-id="element-816">{n.name}</h3>
                  <span className="text-xs font-medium text-gold bg-gold/10 px-2 py-1 rounded whitespace-nowrap" data-id="element-817">
                    {n.price}
                  </span>
                </div>
                <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-818">
                  {n.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Things To Do */}
      <section className="py-24 px-6 bg-white" data-id="element-819">
        <div className="max-w-7xl mx-auto" data-id="element-820">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-821">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-822">
              Life in Henderson
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-823">Things To Do</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-824">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-825">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-826">
                <img src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=2940&auto=format&fit=crop" alt="The District at Green Valley Ranch upscale shopping center in Henderson Nevada" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-827" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-828">
                The District at Green Valley Ranch
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-829">
                An open-air lifestyle center featuring upscale shopping, diverse
                dining, and regular community events in the heart of Henderson.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-830">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-831">
                <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2842&auto=format&fit=crop" alt="Sloan Canyon hiking trails and Lake Mead outdoor recreation near Henderson NV" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-832" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-833">
                Sloan Canyon & Lake Mead
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-834">
                World-class outdoor recreation including Sloan Canyon's ancient
                petroglyphs, Lake Mead boating, and the River Mountains Loop
                Trail.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-835">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-836">
                <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=2940&auto=format&fit=crop" alt="M Resort and Green Valley Ranch luxury resort casinos in Henderson Nevada" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-837" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-838">
                M Resort & Green Valley Ranch
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-839">
                Two luxury resort-casinos offering fine dining, entertainment,
                spas, and pools — all within minutes of Henderson's best
                neighborhoods.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="group" data-id="element-840">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-841">
                <img src="https://images.unsplash.com/photo-1558618666-fcd25c85f82e?q=80&w=2832&auto=format&fit=crop" alt="Cowabunga Bay water park family attraction in Henderson Nevada" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-842" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-843">
                Cowabunga Bay
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-844">
                Henderson's premier seasonal water park on Galleria Drive,
                featuring thrilling water slides, wave pools, lazy rivers, and a
                kids' area — a favorite family destination that replaced the
                former Wet'n'Wild.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="py-24 bg-warmGray px-6 border-t border-warmGray-dark" data-id="element-845">
        <div className="max-w-7xl mx-auto" data-id="element-846">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-847">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-848" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-849">
              Education
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-850">
              Henderson Schools & Education
            </h2>
            <p className="text-charcoal-light font-light max-w-3xl mx-auto text-lg leading-relaxed" data-id="element-851">
              Henderson is home to many of Clark County's highest-performing
              schools, making it one of the most sought-after areas for families
              in Southern Nevada.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-852">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-853">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-854">
                Elementary & Middle
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-855">
                <li data-id="element-856">• John C. Vanderburg Elementary (Blue Ribbon)</li>
                <li data-id="element-857">• Elise L. Wolff Elementary</li>
                <li data-id="element-858">• Lummis Elementary</li>
                <li data-id="element-859">• Bob Miller Middle School</li>
                <li data-id="element-860">• Del E. Webb Middle School</li>
                <li data-id="element-861">• Thurman White Middle School</li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-862">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-863">
                High Schools
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-864">
                <li data-id="element-865">• Coronado High School</li>
                <li data-id="element-866">• Green Valley High School</li>
                <li data-id="element-867">• Foothill High School</li>
                <li data-id="element-868">• Basic High School</li>
                <li data-id="element-869">• Liberty High School</li>
                <li className="pt-4 italic text-xs text-gold" data-id="element-870">
                  Known for strong athletics and advanced placement programs.
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-871">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-872">
                Private & Charter
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-873">
                <li data-id="element-874">• Henderson International School</li>
                <li data-id="element-875">• Bishop Gorman High School (Nearby)</li>
                <li data-id="element-876">• Pinecrest Academy Inspirada</li>
                <li data-id="element-877">• Somerset Academy</li>
                <li data-id="element-878">• Coral Academy of Science</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Parks & Recreation */}
      <section className="py-24 px-6 bg-white" data-id="element-879">
        <div className="max-w-7xl mx-auto" data-id="element-880">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-881">
            <TreePine className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-882" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-883">
              Outdoor Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-884">
              Parks & Recreation
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-885">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-886">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-887">
                <img src="https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=2940&auto=format&fit=crop" alt="Cornerstone Park lake and walking trails in Henderson Nevada" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-888" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-889">
                Cornerstone Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-890">
                A stunning 100-acre park featuring a massive 31-acre lake,
                lighted sports fields, walking trails, and community event
                spaces.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-891">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-892">
                <img src="https://images.unsplash.com/photo-1596140096867-1c1ed1c1e8cd?q=80&w=2940&auto=format&fit=crop" alt="Acacia Park playground and sports courts in Henderson Nevada desert community" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-893" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-894">
                Acacia Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-895">
                A local favorite featuring a skate park, splash pad, sports
                courts, and extensive picnic areas perfect for family
                gatherings.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-896">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-897">
                <img src="https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?q=80&w=2948&auto=format&fit=crop" alt="Henderson Bird Viewing Preserve wetlands nature trails Nevada" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-898" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-899">
                Bird Viewing Preserve
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-900">
                A 147-acre wetlands preserve offering miles of trails and the
                opportunity to spot over 200 species of birds in their natural
                habitat.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="group" data-id="element-901">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-902">
                <img src="https://images.unsplash.com/photo-1472214103451-9374bd1c798e?q=80&w=2940&auto=format&fit=crop" alt="River Mountains Loop Trail cycling and hiking path connecting Henderson to Lake Mead" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-903" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-904">
                River Mountains Loop Trail
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-905">
                A spectacular 34-mile paved loop connecting Henderson to Lake
                Mead and the Hoover Dam, perfect for cycling, running, and
                hiking.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Shopping & Dining */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-906">
        <div className="max-w-7xl mx-auto" data-id="element-907">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-908">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-909">
              <div className="relative h-[600px]" data-id="element-910">
                <img src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2874&auto=format&fit=crop" alt="Fine dining and restaurants in Henderson Nevada Water Street District" className="w-full h-full object-cover" data-id="element-911" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-912"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-913">
              <ShoppingBag className="w-10 h-10 text-gold mb-6" data-id="element-914" />
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-915">
                Lifestyle & Culinary
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-8" data-id="element-916">
                Shopping & Dining
              </h2>

              <div className="space-y-8" data-id="element-917">
                <div data-id="element-918">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-919">
                    <ShoppingBag className="w-5 h-5 text-gold" data-id="element-920" />
                    The District at Green Valley Ranch
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-921">
                    An upscale open-air lifestyle center featuring boutique
                    shopping, fine dining, and community events in a beautiful
                    pedestrian-friendly setting.
                  </p>
                </div>

                <div data-id="element-922">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-923">
                    <ShoppingBag className="w-5 h-5 text-gold" data-id="element-924" />
                    Galleria at Sunset
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-925">
                    A major regional mall offering over 100 stores, department
                    anchors, and diverse dining options for the whole family.
                  </p>
                </div>

                <div data-id="element-926">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-927">
                    <Utensils className="w-5 h-5 text-gold" data-id="element-928" />
                    Water Street District
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-929">
                    Henderson's historic downtown is undergoing a massive
                    revitalization, bringing trendy new restaurants, breweries,
                    and cultural venues to the city's core.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Healthcare */}
      <section className="py-24 px-6 bg-white" data-id="element-930">
        <div className="max-w-4xl mx-auto text-center" data-id="element-931">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-932">
            <Heart className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-933" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-934">
              Wellness
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-935">
              Henderson Healthcare & Wellness
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-8" data-id="element-936">
              Henderson residents benefit from exceptional medical care. The
              city is anchored by the state-of-the-art{' '}
              <strong data-id="element-937">Henderson Hospital</strong>, part of The Valley Health
              System, which opened in 2016.
            </p>
            <p className="text-charcoal-light font-light leading-relaxed text-lg" data-id="element-938">
              The community is also served by multiple{' '}
              <strong data-id="element-939">St. Rose Dominican Hospital</strong> campuses (Siena, San
              Martin, Rose de Lima), Dignity Health medical offices, and
              numerous specialized clinics and urgent care centers throughout
              the city.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Transportation */}
      <section className="py-24 bg-charcoal px-6 text-white" data-id="element-940">
        <div className="max-w-7xl mx-auto" data-id="element-941">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-942">
            <MapPin className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-943" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-944">
              Connectivity
            </span>
            <h2 className="text-4xl font-serif mb-6" data-id="element-945">
              Henderson Transportation & Access
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-946">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-947">
              <Car className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-948" />
              <h4 className="text-xl font-serif mb-2" data-id="element-949">I-215 Beltway</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-950">
                The primary artery connecting Henderson to the airport, the Las
                Vegas Strip, and the west side of the valley.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-951">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-952" />
              <h4 className="text-xl font-serif mb-2" data-id="element-953">I-11 / US-95</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-954">
                Provides a direct, high-speed route to Boulder City, Lake Mead,
                and downtown Las Vegas.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-955">
              <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-956" />
              <h4 className="text-xl font-serif mb-2" data-id="element-957">Harry Reid Airport</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-958">
                Just a 15-minute drive from most Henderson neighborhoods, making
                travel incredibly convenient.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-959">
              <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-960" />
              <h4 className="text-xl font-serif mb-2" data-id="element-961">Henderson Executive</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-962">
                A premier private aviation facility serving corporate and
                private aircraft right in the city.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <RelatedCommunities currentCommunity="Henderson" data-id="element-963" />

      <ListingsComingSoon area="Henderson" data-id="element-964" />

      <ContactCTA data-id="element-965" />
    </main>;
}